// Shared JS for the supplements PDP template (templates/product.supplements.json).
// Only loaded when template.suffix contains 'supplements' (see layout/theme.liquid).
//
// This script loads BEFORE bundle.js/Alpine — never reference window.Alpine
// at parse time, only inside an `alpine:init`/`alpine:initialized` listener.
//
// Keep everything for the supplements PDP in this single file for now
// (per task instructions) rather than splitting into per-section files.

(function () {
  'use strict';

  // --- Supplements nav bar (sections/section-supplements-nav-bar.liquid) ---
  //
  // The nav bar starts in normal static document flow (no `fixed` class),
  // sitting exactly where it naturally falls between main-product and
  // whatever follows. As the page scrolls, we pin it to the top of the
  // viewport only once it would otherwise scroll past it, by watching its
  // own parent element — this section's Shopify-generated wrapper div
  // (`.shopify-section` / `#shopify-section-{section.id}`, i.e.
  // `.supplements-nav`'s `parentElement`; we never assume anything about
  // that wrapper's id/class beyond `parentElement`, since Shopify
  // generates it automatically):
  //
  //   - `.fixed` is added once `parent.getBoundingClientRect().top <= 0`
  //     (the wrapper has scrolled up to the top of the viewport).
  //   - `.fixed` is removed again once `parent.top > 0` (the wrapper is
  //     still below the top of the viewport), returning the nav bar to
  //     normal flow.
  //
  // While `.fixed` is present, the nav bar leaves document flow, so its
  // parent wrapper would otherwise collapse (its only child was the nav
  // bar) and the page would jump. We prevent that by setting the parent's
  // own inline `height` to the nav bar's rendered footprint (including its
  // own margin, which doesn't collapse into the wrapper the way its height
  // does) for as long as `.fixed` is present, and clearing that inline
  // height again the moment `.fixed` is removed, so the parent returns to
  // its natural (auto) height and the nav bar resumes taking up its own
  // space normally in flow.
  //
  // `.header-visible` / `.header-hidden` are toggled independently, in
  // sync with this same function: the theme's compiled scroll listener
  // (bundle.js) only ever toggles nav-up/nav-down on `.header`,
  // `.filter-bar` and `.desktop-collection-list` — those selectors are
  // hardcoded in the compiled bundle and can't be extended — so instead we
  // watch `.header`'s class attribute directly via MutationObserver and
  // mirror its visible/hidden state onto the nav bar. Per the CSS, those
  // classes only have a visible effect while `.fixed` is also present.
  function initSupplementsNavSticky() {
    var navBars = document.querySelectorAll('.supplements-nav');
    if (!navBars.length) return;

    var header = document.querySelector('.header');
    var scheduled = false;
    var pendingForceRemeasure = false;

    function measureNavHeight(nav) {
      // getBoundingClientRect().height is a border-box measurement and
      // does not include margin; the nav bar's own `my-4`/`md:my-5`
      // vertical margin still needs to be accounted for separately, since
      // it doesn't collapse into the wrapper (which has no other content,
      // border, or padding of its own to trap it against).
      var rect = nav.getBoundingClientRect();
      var style = window.getComputedStyle(nav);
      var marginTop = parseFloat(style.marginTop) || 0;
      var marginBottom = parseFloat(style.marginBottom) || 0;
      return rect.height + marginTop + marginBottom;
    }

    function updateNav(nav, forceRemeasure) {
      var parent = nav.parentElement;
      if (!parent) return;

      var shouldBeFixed = parent.getBoundingClientRect().top <= 0;
      var isFixed = nav.classList.contains('fixed');

      if (shouldBeFixed && !isFixed) {
        nav.classList.add('fixed');
        forceRemeasure = true;
      } else if (!shouldBeFixed && isFixed) {
        nav.classList.remove('fixed');
        parent.style.height = '';
        return;
      }

      // Only re-measure (forced layout read) right when the nav bar
      // becomes fixed, or when explicitly asked to (resize — the nav
      // bar's own height can change, e.g. its links wrapping at a
      // narrower width). Skip re-measuring on every scroll frame while
      // already steadily fixed, to avoid unnecessary layout reads.
      if (shouldBeFixed && forceRemeasure) {
        var height = measureNavHeight(nav);
        parent.style.height = height > 0 ? Math.ceil(height) + 'px' : '';
      }
    }

    function updateAllNavs(forceRemeasure) {
      for (var i = 0; i < navBars.length; i++) updateNav(navBars[i], forceRemeasure);
    }

    function scheduleUpdate(forceRemeasure) {
      if (forceRemeasure) pendingForceRemeasure = true;
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(function () {
        var force = pendingForceRemeasure;
        pendingForceRemeasure = false;
        scheduled = false;
        updateAllNavs(force);
      });
    }

    function syncHeaderState() {
      if (!header) return;
      var headerHidden = header.classList.contains('nav-up');
      for (var i = 0; i < navBars.length; i++) {
        navBars[i].classList.toggle('header-hidden', headerHidden);
        navBars[i].classList.toggle('header-visible', !headerHidden);
      }
    }

    // Run once on init (not throttled), in case the page loads already
    // scrolled past the toggle point (e.g. anchor-link navigation, browser
    // scroll restoration).
    syncHeaderState();
    updateAllNavs(false);

    window.addEventListener(
      'scroll',
      function () {
        scheduleUpdate(false);
      },
      { passive: true }
    );

    window.addEventListener('resize', function () {
      scheduleUpdate(true);
    });

    if (header) {
      var observer = new MutationObserver(function () {
        syncHeaderState();
        scheduleUpdate(false);
      });
      observer.observe(header, { attributes: true, attributeFilter: ['class'] });
    }
  }

  // Scroll-spy: bold whichever nav link's target section is currently in
  // view. Links whose `href` has no `#target` fragment (e.g. placeholder
  // `#` links, or links pointing to a different page) are simply skipped —
  // the first link keeps its server-rendered bold/active state by default.
  function initSupplementsNavScrollSpy() {
    if (!('IntersectionObserver' in window)) return;

    var navBars = document.querySelectorAll('.supplements-nav');

    navBars.forEach(function (nav) {
      var links = Array.prototype.slice.call(nav.querySelectorAll('.supplements-nav-link'));
      var targets = links
        .map(function (link) {
          var href = link.getAttribute('href') || '';
          var hashIndex = href.indexOf('#');
          if (hashIndex === -1 || hashIndex === href.length - 1) return null;
          var target = document.getElementById(href.slice(hashIndex + 1));
          return target ? { link: link, target: target } : null;
        })
        .filter(Boolean);

      if (!targets.length) return;

      function setActiveLink(activeLink) {
        links.forEach(function (link) {
          var isActive = link === activeLink;
          link.classList.toggle('font-jost-bold', isActive);
          link.classList.toggle('text-gray-900', isActive);
          link.classList.toggle('font-jost-medium', !isActive);
          link.classList.toggle('text-gray-700', !isActive);
        });
      }

      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            var match = targets.filter(function (t) {
              return t.target === entry.target;
            })[0];
            if (match) setActiveLink(match.link);
          });
        },
        { rootMargin: '-40% 0px -55% 0px', threshold: 0 }
      );

      targets.forEach(function (t) {
        observer.observe(t.target);
      });
    });
  }

  function init() {
    initSupplementsNavSticky();
    initSupplementsNavScrollSpy();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
